/* iter87.199.96 — Billing management panels for the Pro+
   Subscriptions site-admin tab. Composes three collapsible cards:

   1. Grant Comp Pro+     — direct manual grant (source="manual_grant")
   2. Grandfathered Users — read-only roster of legacy Pro+ users
      auto-marked grandfathered when Paddle billing shipped
   3. Paddle Events        — read-only webhook log for triage

   iter87.199.141 — Lemon Squeezy Events panel removed (Lemon Squeezy
   fully deleted from the codebase).

   All three share the same "closed by default, expand-on-click"
   pattern already used elsewhere in ProPlusSubscriptionsTab.jsx
   so operators aren't visually overwhelmed. */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, ChevronDown, ChevronUp, Loader2, Sparkles, Shield, X } from "lucide-react";
import { useBrutalConfirm } from "@/components/BrutalConfirm";
import { api } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

function siteAdminHeaders() {
    const token = localStorage.getItem("site_admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/* ---------- Grant Comp Pro+ ---------- */
function GrantComp() {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [notes, setNotes] = useState("");
    // iter87.281 — Default ON. Renamed from sendInvitation: this now
    // sends OUR OWN Resend email (not a Clerk invitation) — the whole
    // point of the comp flow, without it the comped user has no way
    // to find the Pro+ sign-up page on their own.
    const [sendGrantEmail, setSendGrantEmail] = useState(true);
    const [busy, setBusy] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const submit = async () => {
        if (!email.trim()) return toast.error("Email required");
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/api/site-admin/pro-plus/grant`,
                {
                    email: email.trim().toLowerCase(),
                    notes: notes.trim() || null,
                    send_grant_email: sendGrantEmail,
                },
                { headers: siteAdminHeaders() }
            );
            const ge = r.data?.grant_email || {};
            setLastResult(ge);
            const status = ge?.status;
            if (status === "sent") {
                toast.success(`Pro+ granted + grant email sent to ${email.trim().toLowerCase()}`);
            } else if (status === "failed") {
                toast.error(`Pro+ granted but the grant email failed to send: ${ge?.error || "see logs"}`);
            } else if (status === "skipped") {
                toast.success(`Pro+ granted to ${email.trim().toLowerCase()} — no email sent`);
            } else {
                toast.success(`Pro+ granted to ${email.trim().toLowerCase()}`);
            }
            setEmail(""); setNotes("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Grant failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="brutal-card border-neon/40" data-testid="pro-plus-grant-panel">
            <button
                onClick={() => setOpen(!open)}
                data-testid="pro-plus-grant-toggle"
                className="w-full p-3 flex items-center gap-2 text-left"
            >
                <Sparkles className="w-4 h-4 text-neon" />
                <span className="text-sm font-black tracking-tight">Grant Comp Pro+</span>
                <span className="ml-auto text-[10px] text-white/50 uppercase tracking-widest">
                    Direct grant · bypasses billing
                </span>
                {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {open && (
                <div className="border-t border-neon/20 p-4 space-y-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">
                            Email
                        </label>
                        <input
                            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            data-testid="pro-plus-grant-email"
                            placeholder="friend@example.com"
                            className="brutal-input w-full text-sm"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">
                            Notes (optional)
                        </label>
                        <input
                            type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                            data-testid="pro-plus-grant-notes"
                            placeholder="e.g. Beta tester, family plan, refund replacement"
                            className="brutal-input w-full text-sm"
                        />
                    </div>
                    {/* iter87.281 — Send Pro+ grant email checkbox (was "Send Clerk invitation"). */}
                    <label className="flex items-start gap-2 text-xs text-white/70 cursor-pointer border-t border-white/10 pt-3">
                        <input
                            type="checkbox"
                            checked={sendGrantEmail}
                            onChange={(e) => setSendGrantEmail(e.target.checked)}
                            data-testid="pro-plus-grant-send-invitation"
                            className="mt-0.5 accent-neon"
                        />
                        <span>
                            <strong className="text-white">Send Pro+ grant email</strong>
                            <span className="block text-[10px] text-white/50 mt-0.5">
                                Emails a sign-up/sign-in link with their email pre-filled. Uncheck only if you're telling the user yourself.
                            </span>
                        </span>
                    </label>
                    <button
                        onClick={submit} disabled={busy || !email.trim()}
                        data-testid="pro-plus-grant-submit"
                        className="brutal-btn-primary text-xs px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                        Grant Pro+
                    </button>
                    {lastResult && (
                        <div className="text-[10px] text-white/50 border-t border-white/10 pt-2"
                             data-testid="pro-plus-grant-last-result">
                            Grant email: <strong className="text-white/80">{lastResult.status || "skipped"}</strong>
                            {lastResult.link_type ? ` · ${lastResult.link_type === "sign_in" ? "sign-in link" : "sign-up link"}` : ""}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* ---------- Grandfathered roster ---------- */
function Grandfathered() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();
    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(
                `${API}/api/site-admin/pro-plus/grandfathered`,
                { headers: siteAdminHeaders() }
            );
            setItems(r.data?.items || []);
        } catch (e) {
            toast.error("Failed to load grandfathered roster");
        } finally { setBusy(false); }
    };
    // iter87.199.103 — Remove a row from the grandfathered roster.
    // Flips `grandfathered: false` server-side; the sub stays live.
    const remove = async (row) => {
        const ok = await brutalConfirm({
            title: "Remove from grandfathered?",
            body: (
                <div className="space-y-2 text-sm">
                    <p>
                        <span className="font-mono">{row.email}</span> will lose
                        their revocation immunity but keep Pro+ for now.
                    </p>
                    <p className="text-white/60 text-xs">
                        Future Paddle close events (`subscription.expired`,
                        `subscription.canceled`) will now be honored on this
                        subscription. Use the Delete button in Activity for full
                        removal.
                    </p>
                </div>
            ),
            confirmLabel: "Remove immunity",
            confirmVariant: "danger",
        });
        if (!ok) return;
        setRemovingId(row.id);
        try {
            await api.proPlusUngrandfather(row.id);
            setItems((prev) => prev.filter((x) => x.id !== row.id));
            toast.success(`${row.email} removed from grandfathered`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to remove");
        } finally {
            setRemovingId(null);
        }
    };
    return (
        <div className="brutal-card border-cyber/40" data-testid="pro-plus-grandfathered-panel">
            {confirmHost}
            <button
                onClick={() => { if (!open && items.length === 0) load(); setOpen(!open); }}
                data-testid="pro-plus-grandfathered-toggle"
                className="w-full p-3 flex items-center gap-2 text-left"
            >
                <Shield className="w-4 h-4 text-cyber" />
                <span className="text-sm font-black tracking-tight">Grandfathered Users</span>
                <span className="ml-auto text-[10px] text-white/50 uppercase tracking-widest">
                    Pre-billing Pro+ · immune to revocation
                </span>
                {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {open && (
                <div className="border-t border-cyber/20 p-4 space-y-2" data-testid="pro-plus-grandfathered-list">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {!busy && items.length === 0 && (
                        <div className="text-xs text-white/50">No grandfathered users.</div>
                    )}
                    {items.map((it) => (
                        <div key={it.id} className="text-xs flex items-center gap-3"
                             data-testid={`pro-plus-grandfathered-${it.email}`}>
                            <span className="font-mono flex-1 truncate">{it.email}</span>
                            <span className="text-white/40">since {it.grandfathered_at?.slice(0, 10) || "—"}</span>
                            <span className="pill text-[9px] border-cyber/40 text-cyber uppercase">
                                {it.status}
                            </span>
                            <button
                                type="button"
                                onClick={() => remove(it)}
                                disabled={removingId === it.id}
                                data-testid={`pro-plus-grandfathered-remove-${it.id}`}
                                aria-label={`Remove ${it.email} from grandfathered`}
                                title="Remove from grandfathered roster"
                                className="brutal-btn-ghost text-[10px] py-1 px-2 text-red-400 hover:bg-red-500/10 disabled:opacity-40"
                            >
                                {removingId === it.id
                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                    : <X className="w-3 h-3" />}
                            </button>
                        </div>
                    ))}
                    <div className="text-[10px] text-white/40 pt-2 border-t border-white/10">
                        Total: {items.length}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------- Paddle webhook events ---------- */
function PaddleEvents() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(
                `${API}/api/site-admin/pro-plus/paddle-events`,
                { headers: siteAdminHeaders() }
            );
            setItems(r.data?.items || []);
        } catch (e) {
            toast.error("Failed to load Paddle event log");
        } finally { setBusy(false); }
    };
    return (
        <div className="brutal-card border-volt/40" data-testid="pro-plus-paddle-events-panel">
            <button
                onClick={() => { if (!open) load(); setOpen(!open); }}
                data-testid="pro-plus-paddle-events-toggle"
                className="w-full p-3 flex items-center gap-2 text-left"
            >
                <CreditCard className="w-4 h-4 text-volt" />
                <span className="text-sm font-black tracking-tight">Paddle Events</span>
                <span className="ml-auto text-[10px] text-white/50 uppercase tracking-widest">
                    Webhook log · read-only
                </span>
                {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {open && (
                <div className="border-t border-volt/20 p-4 space-y-3" data-testid="pro-plus-paddle-events-list">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {!busy && items.length === 0 && (
                        <div className="text-xs text-white/50">No events received yet.</div>
                    )}
                    {items.map((it, idx) => (
                        <div key={it.event_id || `${it.received_at || "row"}-${idx}`}
                             className="text-xs flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 pb-2 border-b border-white/5 last:border-b-0 last:pb-0"
                             data-testid={`pro-plus-paddle-event-${it.event_id}`}>
                            <span className="font-mono text-white/80 truncate sm:flex-1 sm:min-w-0">{it.email || "—"}</span>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="pill text-[9px] border-volt/40 text-volt uppercase whitespace-nowrap">
                                    {it.event_name}
                                </span>
                                <span className={`pill text-[9px] uppercase whitespace-nowrap ${it.processed ? "border-neon/40 text-neon" : "border-white/20 text-white/50"}`}>
                                    {it.processed ? (it.action || "ok") : "pending"}
                                </span>
                                <span className="text-white/40 whitespace-nowrap">{it.received_at?.slice(0, 16).replace("T", " ")}</span>
                            </div>
                        </div>
                    ))}
                    {items.length > 0 && (
                        <div className="text-[10px] text-white/40 pt-2 border-t border-white/10">
                            Showing latest {items.length} events
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function ProPlusBillingSection() {
    return (
        <section className="space-y-3" data-testid="pro-plus-billing-section">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-black pt-1">
                Billing · Paddle
            </div>
            <GrantComp />
            <Grandfathered />
            <PaddleEvents />
        </section>
    );
}
